import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Virtual } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import LimitedEditionIcon from '@/assets/all-icons/limited-edition-icon.jsx';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useGetSearchParams from '@/hooks/useGetSearchParams';
import useProtectActionUnregistered from '@/hooks/useProtectActionUnregistered';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import GalleryOffersService from '@/services/galleryOffers.service';
import LimitedEditionOwnersPopup from '@/shared-components/artwork/limited-edition-summary/LimitedEditionOwnersPopup';
import LimitedEditionOwnersPopupMobile from '@/shared-components/artwork/limited-edition-summary/LimitedEditionOwnersPopupMobile';
import Loader from '@/shared-components/loader/Loader';
import GenericBottomSheet from '@/shared-components/popups/components/GenericBottomSheet';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import UnregisteredPopup from '@/shared-components/popups/unregistered-user-popup';
import AuctionUnregisteredPopup from '@/shared-components/popups/unregistered-user-popup/auction-unregistered-popup';
import Utils from '@/utils';
import { isRestrictedArtwork } from '@/utils/artworkVisibility';
import detectStandaloneVisit from '@/utils/detectStandaloneVisit';

import ArtworkMetaDesc from './components/ArtworkMetaDesc';
import ArtworkNotFound from './components/ArtworkNotFound';
import DesktopControls from './content-section/components/DesktopControls';
import useArtwork from './hooks/useArtwork';
import useArtworkSlider from './hooks/useArtworkSlider';
import useArtworkViewContext from './hooks/useArtworkViewContext';
import useLimitedEditionCollectors from './hooks/useLimitedEditionCollectors';
import ArtworkAuctionMobile from './mobile-view/components/artwork-auction/ArtworkAuctionMobile';
import ArtworkMoreDetails from './mobile-view/components/artwork-more-details/ArtworkMoreDetails';
import ArtworkViewHeader from './mobile-view/components/artwork-view-header/ArtworkViewHeader';
import {
  getArtworkHoldingType,
  getArtworkLimitedEditionOptions,
  getLimitedEditionSizeMap,
} from './utils/artworkView.selectors';
import { buildActiveOfferMap, getArtworkIdentityKeys } from './utils/offer.utils';
import ContentSection from './content-section';
import ArtworkMobileView from './mobile-view';
import PicturesSection from './pictures-section';

import './index.css';

const COMPACT_ARTWORK_LAYOUT_QUERY = '(max-width: 778px)';

function ArtworkSlide({
  artworkData,
  isActive,
  hasPrev,
  hasNext,
  displayLoader,
  detailsOpen,
  setDetailsOpen,
  openLimitedEditionCollectors,
  useAuctionVariant = false,
  isStandaloneVisit = false,
}) {
  const { state } = useStateHandler();
  const isMobileLayout = useMediaQuery({ query: COMPACT_ARTWORK_LAYOUT_QUERY });
  const viewerSessionId = state?.user_session?._id ?? state?.user_session?.id ?? null;
  const shouldLoadViewerContext = Boolean(viewerSessionId) && isActive;
  const shouldLoadPurchaseContext = !isMobileLayout || detailsOpen || useAuctionVariant;
  const shouldLoadOwnershipContext = !isMobileLayout || detailsOpen || useAuctionVariant;
  const {
    socialsData,
    setSocialsData,
    viewerContext,
    purchaseContext,
    ownershipContext,
  } = useArtworkViewContext({
    artworkSlug: artworkData.slug,
    isActive,
    viewerSessionId,
    socialSummary: artworkData.social_summary,
    shouldLoadViewerContext,
    shouldLoadPurchaseContext: isActive && shouldLoadPurchaseContext,
    shouldLoadOwnershipContext: isActive && shouldLoadOwnershipContext,
  });
  const scrollableDiv = useRef();
  const { onTouchStart, onTouchEnd, onWheel } = useArtworkSlider(scrollableDiv);
  const { showPopup } = useProtectActionUnregistered({
    typeOfProtection: "USER_LIMIT_VIEW",
    popupDelayMs: useAuctionVariant ? 0 : undefined,
  });
  const [params] = useGetSearchParams({ validParams: ["rate", "thought"] });
  const [selectedSocial, setSelectedSocial] = useState(() => {
    if(params?.rate) return "ratings";
    if(params?.thought) return "thoughts";
    return null;
  });
  const artworkId = artworkData?._id ?? null;
  const [limitedEditionPreviewState, setLimitedEditionPreviewState] = useState(() => ({
    isVisible: false,
    selectedSizeKey: null,
    selectedSizeData: null,
    selectedFrameKey: null,
    selectedMuseumLabelKey: null,
    artworkId,
  }));
  const [aiMessages, setAiMessages] = useState([]);

  // Reset the preview helper whenever the active artwork changes so the slider returns to the default view.
  useEffect(() => {
    setLimitedEditionPreviewState({
      isVisible: false,
      selectedSizeKey: null,
      selectedSizeData: null,
      selectedFrameKey: null,
      selectedMuseumLabelKey: null,
      artworkId,
    });
    setAiMessages([]);
  }, [artworkId]);

  const mergedArtworkData = useMemo(() => ({
    ...artworkData,
    ...(ownershipContext?.ownership_summary
      ? { ownership_summary: ownershipContext.ownership_summary }
      : {}),
    ...(viewerContext ? { viewer_context: viewerContext } : {}),
    ...(purchaseContext ? { purchase_context: purchaseContext } : {}),
    ...(ownershipContext ? { ownership_context: ownershipContext } : {}),
  }), [
    artworkData,
    ownershipContext?.ownership_summary,
    ownershipContext,
    purchaseContext,
    viewerContext,
  ]);

  return (
    <ArtworkDataContext.Provider
      value={{
        artworkData: mergedArtworkData,
        artworkViewerContext: viewerContext,
        artworkPurchaseContext: purchaseContext,
        artworkOwnershipContext: ownershipContext,
        socialsData,
        setSocialsData,
        aiMessages,
        setAiMessages,
        limitedEditionPreviewState,
        setLimitedEditionPreviewState,
        openLimitedEditionCollectors,
      }}
    >
      {showPopup && (
        useAuctionVariant ? (
          <AuctionUnregisteredPopup showPopUp={showPopup} />
        ) : (
          <UnregisteredPopup showPopup={showPopup} />
        )
      )}
      <ArtworkMetaDesc artworkData={artworkData} />
      
      <div
        className={`artwork__container remove-scrollbar${isStandaloneVisit ? ' artwork__container--standalone' : ''}`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
        ref={scrollableDiv}
      >
        <div className="artwork__wrap">
          {isMobileLayout ? (
            <ArtworkMobileView
              selectedSocial={selectedSocial}
              setSelectedSocial={setSelectedSocial}
              detailsOpen={detailsOpen}
              setDetailsOpen={setDetailsOpen}
              isStandaloneVisit={isStandaloneVisit}
            />
          ) : (
            <>
              <PicturesSection/>
              <DesktopControls hasPrev={hasPrev} hasNext={hasNext}/>
              <ContentSection
                selectedSocial={selectedSocial}
                setSelectedSocial={setSelectedSocial}
              />
            </>
          )}
        </div>

        {displayLoader && (
          <Loader className="artwork__spinner" thin />
        )}
      </div>
    </ArtworkDataContext.Provider>
  );
}

export default function Artwork() {
  const { loading, error, artworks, shouldLoadMore, activeSlide, setActiveSlide } = useArtwork();
  const tArtworkView = useTranslations('common.shared.artwork_view.mobile');
  const { state, stateHandler } = useStateHandler();
  const location = useLocation();
  const navigate = useNavigate();
  const { slug: routeArtworkSlug } = useParams();
  const isDevice = useRef(Utils.isDevice());
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [auctionOpen, setAuctionOpen] = useState(false);
  const isMobileLayout = useMediaQuery({ query: COMPACT_ARTWORK_LAYOUT_QUERY });
  const [isDirectAuctionEntry, setIsDirectAuctionEntry] = useState(false);
  const [isStandaloneVisit, setIsStandaloneVisit] = useState(() => detectStandaloneVisit());
  const swiperRef = useRef(null);
  const [viewerOffersByArtworkId, setViewerOffersByArtworkId] = useState({});
  const loadedViewerOffersSessionRef = useRef(null);

  const viewerSessionId = state?.user_session?._id ?? state?.user_session?.id ?? null;
  const shouldLoadViewerOffers = Boolean(
    viewerSessionId
    && (!isMobileLayout || detailsOpen || auctionOpen)
  );

  useEffect(() => {
    let active = true;

    if (!viewerSessionId) {
      setViewerOffersByArtworkId({});
      loadedViewerOffersSessionRef.current = null;
      return () => {
        active = false;
      };
    }

    if (!shouldLoadViewerOffers) {
      return () => {
        active = false;
      };
    }

    if (loadedViewerOffersSessionRef.current === viewerSessionId) {
      return () => {
        active = false;
      };
    }

    async function loadViewerOffers() {
      try {
        const { response, data } = await GalleryOffersService.getMyGalleryOffers({
          status: 'open',
          limit: 40,
        });

        if (!active) {
          return;
        }

        if (!response?.ok) {
          setViewerOffersByArtworkId({});
          return;
        }

        loadedViewerOffersSessionRef.current = viewerSessionId;
        setViewerOffersByArtworkId(buildActiveOfferMap(data?.offers));
      } catch (_error) {
        if (active) {
          setViewerOffersByArtworkId({});
        }
      }
    }

    void loadViewerOffers();

    return () => {
      active = false;
    };
  }, [auctionOpen, detailsOpen, isMobileLayout, shouldLoadViewerOffers, viewerSessionId]);

  const artworkSlides = useMemo(() => {
    const source = Array.isArray(artworks?.data) ? artworks.data : [];

    return source.map((artworkData) => {
      const activeViewerOffer = getArtworkIdentityKeys(artworkData)
        .map((identityKey) => viewerOffersByArtworkId[identityKey] ?? null)
        .find(Boolean) ?? null;

      if (!activeViewerOffer) {
        return artworkData;
      }

      return {
        ...artworkData,
        _activeViewerOffer: activeViewerOffer,
      };
    });
  }, [artworks?.data, viewerOffersByArtworkId]);

  const activeArtworkData = artworkSlides?.[activeSlide] || artworkSlides?.[0];
  const shouldAutoOpenLimitedEditionCollectors = useMemo(
    () => (
      new URLSearchParams(location.search).get('limited_edition_owners') === '1'
      && (!routeArtworkSlug || routeArtworkSlug === activeArtworkData?.slug)
    ),
    [activeArtworkData?.slug, location.search, routeArtworkSlug],
  );
  const openAuthentication = useCallback(() => {
    // This page-level controller must remain mounted while the mobile details sheet is closed.
    stateHandler.set('showAuthPopup', 'login');
  }, [stateHandler]);
  const {
    collectors: limitedEditionCollectors,
    openCollectors: openLimitedEditionCollectors,
    ownersPopupOpen,
    closeOwnersPopup,
    suartePlusPopupOpen,
    closeSuartePlusPopup,
    handleSuartePlusActivated,
  } = useLimitedEditionCollectors({
    artworkSlug: activeArtworkData?.slug,
    viewer: state.user_session,
    onAuthenticationRequired: openAuthentication,
    autoOpen: shouldAutoOpenLimitedEditionCollectors,
  });

  const activeArtworkIsAuction = useMemo(() => {
    const about = activeArtworkData?.artwork_about;
    const auction = activeArtworkData?.artwork_auction;
    if (!about && !auction) {
      return false;
    }
    if (about?.artwork_is_auction) {
      return true;
    }
    return Boolean(auction?.is_enabled || auction);
  }, [activeArtworkData?.artwork_about, activeArtworkData?.artwork_auction]);

  const shouldUseAuctionPopup = isDirectAuctionEntry && activeArtworkIsAuction;

  const { privateActionHandlerUnregistered, showPopup } = useProtectActionUnregistered({
    typeOfProtection: "USER_LIMIT_VIEW",
    popupDelayMs: shouldUseAuctionPopup ? 0 : undefined,
  });

  const holdingType = getArtworkHoldingType(activeArtworkData);
  const limitedEditionOptions = useMemo(
    () => getArtworkLimitedEditionOptions(activeArtworkData, {
      fallbackHoldingMetadata: holdingType === 'limited_edition',
    }),
    [activeArtworkData, holdingType],
  );

  const hasLimitedEditionAvailable = useMemo(() => {
    const editions = limitedEditionOptions;
    if (!editions || typeof editions !== 'object') {
      return false;
    }

    return Object.values(editions).some((edition) => {
      if (!edition) {
        return false;
      }

      const { quantity, sold } = edition;
      if (typeof quantity === 'number' && typeof sold === 'number') {
        return quantity > sold;
      }

      return true;
    });
  }, [limitedEditionOptions]);
  const hasLimitedEditionVariants = useMemo(() => {
    if (!limitedEditionOptions || typeof limitedEditionOptions !== 'object') {
      return false;
    }
    return Object.values(limitedEditionOptions).some((edition) => Boolean(edition));
  }, [limitedEditionOptions]);
  const isLimitedEditionHolding = holdingType === 'limited_edition';
  const limitedEditionSizeMap = useMemo(
    () => getLimitedEditionSizeMap(limitedEditionOptions),
    [limitedEditionOptions],
  );
  const isAdminOnlyArtwork = useMemo(
    () => isRestrictedArtwork(activeArtworkData),
    [activeArtworkData],
  );

  const auctionCta = useMemo(() => {
    const about = activeArtworkData?.artwork_about ?? {};
    const auction = activeArtworkData?.artwork_auction ?? {};
    const isAuction = Boolean(about.artwork_is_auction);
    if (!isAuction) {
      return {
        isAuction: false,
        label: 'See auction',
        isClosed: false,
      };
    }

    const isEnabled = Boolean(auction.is_enabled);
    const hasClosedFlag = Boolean(auction.is_closed ?? auction.closed);
    const endsAtTimestamp = auction?.ends_at ? new Date(auction.ends_at).getTime() : NaN;
    const status = typeof (auction.state || auction.status) === 'string'
      ? (auction.state || auction.status).trim().toLowerCase()
      : '';
    const endedBySchedule = Number.isFinite(endsAtTimestamp) && endsAtTimestamp <= Date.now();
    const isClosed = Boolean(
      hasClosedFlag ||
      status === 'closed' ||
      status === 'ended' ||
      (endedBySchedule && !isEnabled)
    );

    return {
      isAuction: true,
      label: isClosed ? 'Auction Details' : 'See auction',
      isClosed,
    };
  }, [activeArtworkData?.artwork_about, activeArtworkData?.artwork_auction]);

  // Hide app navigation when in artwork view
  useEffect(() => {
    document.body.classList.add('hide-app-nav');
    return () => {
      document.body.classList.remove('hide-app-nav');
    };
  }, []);

  useEffect(() => {
    setAuctionOpen(false);
    setDetailsOpen(false);
  }, [activeArtworkData?._id]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const body = document.body;
    const browserClass = 'artwork-browser-visit';
    const standaloneClass = 'artwork-standalone-visit';

    if (isStandaloneVisit) {
      body.classList.add(standaloneClass);
      body.classList.remove(browserClass);
    } else {
      body.classList.add(browserClass);
      body.classList.remove(standaloneClass);
    }

    return () => {
      body.classList.remove(browserClass);
      body.classList.remove(standaloneClass);
    };
  }, [isStandaloneVisit]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateStandalone = () => {
      setIsStandaloneVisit(detectStandaloneVisit());
    };

    updateStandalone();

    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const queries = ['(display-mode: standalone)', '(display-mode: fullscreen)', '(display-mode: minimal-ui)'];
    const listeners = [];

    queries.forEach((query) => {
      let mql;
      try {
        mql = window.matchMedia(query);
      } catch (_error) {
        mql = null;
      }
      if (!mql) {
        return;
      }
      const handler = () => updateStandalone();
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handler);
        listeners.push(() => mql.removeEventListener('change', handler));
      } else if (typeof mql.addListener === 'function') {
        mql.addListener(handler);
        listeners.push(() => mql.removeListener(handler));
      }
    });

    return () => {
      listeners.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (_error) {
          // no-op
        }
      });
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const referrer = document.referrer;
    if (!referrer) {
      setIsDirectAuctionEntry(true);
      return;
    }
    try {
      const origin = new URL(referrer).origin;
      setIsDirectAuctionEntry(origin !== window.location.origin);
    } catch (_error) {
      setIsDirectAuctionEntry(false);
    }
  }, []);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || swiper.destroyed) {
      return;
    }

    const maxIndex = Math.max((artworks?.data?.length ?? 1) - 1, 0);
    const clampedIndex = Math.min(Math.max(activeSlide ?? 0, 0), maxIndex);

    if (swiper.activeIndex !== clampedIndex) {
      swiper.slideTo(clampedIndex, 0, false);
    }
  }, [activeSlide, artworks?.data?.length]);

  function handleClick(event) {
    privateActionHandlerUnregistered(event);
    if (event.defaultPrevented) {
      return;
    }
  }

  if (loading) { 
    return <Loader />;
  } else if (error) {
    return <ArtworkNotFound />;
  } else {
    return (
      <>
        {/* Fixed header shown across all artwork slides (outside Swiper to avoid transform issues) */}
        <ArtworkViewHeader artworkData={activeArtworkData} />
        {showPopup && (
          shouldUseAuctionPopup ? (
            <AuctionUnregisteredPopup showPopUp={showPopup} />
          ) : (
            <UnregisteredPopup showPopup={showPopup} />
          )
        )}
        <Swiper 
          className="artwork__swiper"
          direction='vertical'
          allowTouchMove={true}
          touchRatio={1}
          speed={isDevice.current ? 400 : 900}
          noSwiping={true}
          noSwipingClass="artwork-slider-lock"
          onInit={(swiper) => {
            swiperRef.current = swiper;
            swiper.slideTo(activeSlide, 0);
          }}
          onSlideChange={swiper => setActiveSlide(swiper.activeIndex) }
          modules={[Virtual]}
          onTransitionEnd={handleClick}
          virtual
        >
          {artworkSlides.map((artworkData, index) => {
            const isAuctionArtwork = Boolean(
              artworkData?.artwork_about?.artwork_is_auction ||
              artworkData?.artwork_auction?.is_enabled ||
              artworkData?.artwork_auction
            );
            const useAuctionVariant = isDirectAuctionEntry && isAuctionArtwork;
            return (
              <SwiperSlide
                key={artworkData.product_id ? artworkData.product_id : artworkData._id}
                virtualIndex={index}
              >
                <ArtworkSlide
                  artworkData={artworkData}
                  isActive={index === activeSlide}
                  hasPrev={index !== 0}
                  hasNext={index !== artworks.data.length - 1}
                  displayLoader={shouldLoadMore && index === artworks.data.length - 1}
                  detailsOpen={detailsOpen}
                  setDetailsOpen={setDetailsOpen}
                  openLimitedEditionCollectors={openLimitedEditionCollectors}
                  useAuctionVariant={useAuctionVariant}
                  isStandaloneVisit={isStandaloneVisit}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        {isMobileLayout && (
          <div
            className={`artwork-view-footer${
              auctionCta.isAuction ? ' artwork-view-footer--auction' : ''
            }`}
          >
            <button
              type="button"
              className="artwork-view-footer__button artwork-view-more-details-button"
              onClick={() => {
                const activeArtworkSlug =
                  artworks?.data?.[activeSlide]?.slug
                  ?? artworks?.data?.[activeSlide]?.product_slug
                  ?? artworks?.data?.[activeSlide]?.artwork_slug
                  ?? null;
                if (activeArtworkSlug) {
                  trackArtworkPreferenceInteraction({
                    slug: activeArtworkSlug,
                    signal: ARTWORK_PREFERENCE_SIGNALS.MORE_DETAILS_MOBILE,
                    metadata: { source: 'artwork-view-footer__button artwork-view-more-details-button' },
                  });
                }
                setAuctionOpen(false);
                setDetailsOpen(true);
              }}
            >
              <span>{tArtworkView('more_details_button')}</span>
              {hasLimitedEditionAvailable || hasLimitedEditionVariants || isLimitedEditionHolding ? (
                <LimitedEditionIcon className="artwork-view-more-details-button__icon" />
              ) : null}
            </button>
            {auctionCta.isAuction && (
              <button
                type="button"
                className="artwork-view-footer__button artwork-view-see-auction-button"
                onClick={() => {
                  setDetailsOpen(false);
                  setAuctionOpen(true);
                }}
              >
                <span>{auctionCta.label}</span>
              </button>
            )}
          </div>
        )}

        {/* Single bottom-sheet bound to the active artwork */}
        <GenericBottomSheet
          className="artwork-more-details__bottom-sheet"
          open={detailsOpen}
          close={() => setDetailsOpen(false)}
          onTouchStart={(event) => event.stopPropagation()}
          onTouchEnd={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
        >
          <ArtworkMoreDetails
            artworkData={artworkSlides[activeSlide]}
            onOpenLimitedEditionCollectors={openLimitedEditionCollectors}
          />
        </GenericBottomSheet>

        {auctionCta.isAuction && (
          <GenericBottomSheet
            className="artwork-auction__bottom-sheet"
            open={auctionOpen}
            close={() => setAuctionOpen(false)}
            onTouchStart={(event) => event.stopPropagation()}
            onTouchEnd={(event) => event.stopPropagation()}
            onWheel={(event) => event.stopPropagation()}
          >
            <ArtworkAuctionMobile
              open={auctionOpen}
              onClose={() => setAuctionOpen(false)}
              artworkData={artworkSlides[activeSlide]}
            />
          </GenericBottomSheet>
        )}
        {isMobileLayout ? (
          <LimitedEditionOwnersPopupMobile
            open={ownersPopupOpen}
            onClose={closeOwnersPopup}
            owners={limitedEditionCollectors.owners}
            editionSizes={limitedEditionSizeMap}
            editionSoldCounts={limitedEditionCollectors.sold_counts}
            forceSequentialEditionNumbers={isAdminOnlyArtwork}
            forceDiscontinuedCounts={isAdminOnlyArtwork}
            discontinuedTotal={isAdminOnlyArtwork ? limitedEditionCollectors.sold_count : null}
            onNavigateProfile={(username) => navigate(`/user/${username}`)}
          />
        ) : (
          <LimitedEditionOwnersPopup
            open={ownersPopupOpen}
            onClose={closeOwnersPopup}
            owners={limitedEditionCollectors.owners}
            editionSizes={limitedEditionSizeMap}
            editionSoldCounts={limitedEditionCollectors.sold_counts}
            forceSequentialEditionNumbers={isAdminOnlyArtwork}
            forceDiscontinuedCounts={isAdminOnlyArtwork}
            discontinuedTotal={isAdminOnlyArtwork ? limitedEditionCollectors.sold_count : null}
            onNavigateProfile={(username) => navigate(`/user/${username}`)}
          />
        )}
        <SuartePlusPopup
          open={suartePlusPopupOpen}
          onClose={closeSuartePlusPopup}
          onSuccess={handleSuartePlusActivated}
          actionTitle="View Collectors"
          highlightedBenefit="limited_edition_collectors"
        />
      </>
    );
  }
}
